import React from "react";
// @ts-ignore-next-line
import { Auth0LockPasswordless, Auth0UserProfile } from "auth0-lock";
import { useLocation, useHistory } from "react-router-dom";
import apiClient from "./helpers/api";
import { setItem, getItem } from "./helpers/storage";
import config from "./config";

const passwordlessOptions = {
  allowedConnections: ["email"],
  passwordlessMethod: "code",
  auth: {
    redirectUrl: config.auth0.redirectUri,
    responseType: "token id_token",
    params: {
      scope: "openid profile email"
    },
    audience: config.auth0.audience
  },
  theme: {
    logo: "https://images.startingstrengthgyms.com/squatman+150x150.png",
    primaryColor: "#EE2737"
  },
  languageDictionary: {
    title: "Starting Strength Logbook Admin"
  },
  closable: false
};

const REDIRECT_TO = "REDIRECT_TO";

const lockPasswordless = new Auth0LockPasswordless(
  config.auth0.clientId,
  config.auth0.domain,
  passwordlessOptions
);

const mapErrors: any = {
  invalid_request: "Missing code. Please try again.",
  invalid_user_password: "The code you entered is invalid. Please try again."
};

interface IProps {
  children?: React.ReactNode;
}

interface IAuthContext {
  isAuthenticated: boolean;
  user: Auth0UserProfile | undefined;
  login: () => void;
  loading: boolean;
  logout: () => void;
}

export const AuthContext = React.createContext<IAuthContext | undefined>(
  undefined
);
export const useAuth = () => React.useContext(AuthContext) as IAuthContext;

// Dev bypass NEVER activates in a production build, even if the env var
// somehow ends up in the prod .env. Defense-in-depth so a misconfigured
// build cannot ship an authless dashboard.
//
// To simulate a leadership user locally, set REACT_APP_BYPASS_AUTH=true
// and REACT_APP_BYPASS_EMAIL to one of the addresses in src/lib/access.ts.
const BYPASS_AUTH =
  process.env.NODE_ENV !== "production" &&
  process.env.REACT_APP_BYPASS_AUTH === "true";

const fakeUser = {
  name: "Dev User",
  nickname: "dev",
  email: process.env.REACT_APP_BYPASS_EMAIL || "coach@local",
  email_verified: true,
  sub: "dev|local"
} as Auth0UserProfile;

function BypassAuthProvider({ children }: IProps) {
  React.useEffect(() => {
    apiClient.setToken("dev-bypass-token");
  }, []);
  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: true,
        user: fakeUser,
        loading: false,
        login: () => undefined,
        logout: () => undefined
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function RealAuthProvider({ children }: IProps) {
  const history = useHistory();
  const location = useLocation();

  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [user, setUser] = React.useState<Auth0UserProfile>();
  const [loading, setLoading] = React.useState(true);

  const logout = async () => {
    apiClient.setToken("");

    lockPasswordless.logout({ returnTo: config.auth0.redirectUri });
  };

  /**
   * Renew the access token mid-session, without any of checkSession's
   * first-load behaviour: no stored-redirect replay (which would navigate the
   * coach away from whatever they were doing) and no loading-state changes.
   *
   * Resolves false on any failure, which lets the API client fall through to
   * onUnauthorized and end the session as before.
   */
  const renewToken = (): Promise<boolean> =>
    new Promise(resolve => {
      lockPasswordless.checkSession({}, (err: any, authResult: any) => {
        if (err || !authResult || !authResult.accessToken) {
          resolve(false);

          return;
        }

        apiClient.setToken(authResult.accessToken);
        resolve(true);
      });
    });

  apiClient.onTokenExpired = renewToken;
  apiClient.onUnauthorized = () => logout();

  const handleAuthenticated = (authResult: any) => {
    setUser(authResult.idTokenPayload);
    apiClient.setToken(authResult.accessToken);
    setIsAuthenticated(true);
    setLoading(false);
  };

  lockPasswordless.on("authorization_error", (error: any) => {
    const messageHtml = document.querySelector(
      ".auth0-global-message > span > span"
    );
    const errorText = mapErrors[error.error]
      ? mapErrors[error.error]
      : error.description;
    if (messageHtml) {
      messageHtml.textContent = errorText;
    }
  });

  lockPasswordless.on("authenticated", (authResult: any) => {
    console.log(
      "authenticatedauthenticatedauthenticatedauthenticatedauthenticatedauthenticated"
    );
    console.log(authResult);
    handleAuthenticated(authResult);
    setTimeout(() => lockPasswordless.hide(), 2000);
  });

  const checkSession = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      lockPasswordless.checkSession({}, async (err: any, authResult: any) => {
        console.log(
          "checkSessioncheckSessioncheckSessioncheckSessioncheckSessioncheckSession"
        );
        console.log(authResult);
        window.location.hash = "";
        if (err) {
          setLoading(false);
          reject(new Error(err));

          return;
        }

        if (!authResult || !authResult.idToken || !authResult.accessToken) {
          reject(new Error("Invalid token"));

          return;
        }

        handleAuthenticated(authResult);

        const redirectTo = getItem(REDIRECT_TO);

        if (redirectTo) {
          setItem(REDIRECT_TO, "");
          history.push(redirectTo);
        }

        resolve("Authenticated");
      });
    });
  };

  React.useEffect(() => {
    if (!loading && !isAuthenticated) {
      if (location.pathname !== "/") {
        setItem(REDIRECT_TO, location.pathname);
      }
      login();
    }
    // eslint-disable-next-line
  }, [isAuthenticated, loading]);

  const login = () => {
    lockPasswordless.show();
  };

  React.useEffect(() => {
    if (location.pathname !== "/login") {
      checkSession();
    }
    // eslint-disable-next-line
  }, []);

  // Pilot access gating lives in src/lib/access.ts (hardcoded leadership
  // allowlist by email). The Auth0 custom claim plumbing (pike13_role,
  // subdomain, coach_person_id) was removed when we shipped without the
  // matching Auth0 Action; restore it when the proper auth path lands —
  // see docs/coach-dashboard-pilot-access.md.

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        user,
        loading,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

const AuthProvider = ({ children }: IProps) =>
  BYPASS_AUTH ? (
    <BypassAuthProvider>{children}</BypassAuthProvider>
  ) : (
    <RealAuthProvider>{children}</RealAuthProvider>
  );

export default AuthProvider;
